import type {
  Project,
  WorkspaceVersion,
  Layer,
  Rect,
  DiffusionParams,
  GenerationTask,
  ProjectSuccessAnalysis,
  ProjectSuccessWorkspaceAnalysis,
  ProjectSuccessLayerGenealogy,
  ProjectSuccessLayerTimelineStep,
  ProjectSuccessLayerInfo,
  ProjectSuccessVersionInfo,
  ProjectSuccessLayerChangeType,
  ProjectSuccessLayerGenerationMetadata,
  StrippedLayer,
  GenerationTaskSnapshot
} from '#types/types';
import { normalizeSnapshotUrl } from '#lib/annotation-snapshot';

type ProjectWorkspace = NonNullable<Project['workspaces']>[number];
type ProjectLayer = NonNullable<Project['layers']>[number];

type ProjectSuccessForensicsOptions = {
  projectName?: string;
  snapshots?: GenerationTaskSnapshot[];
};

const UNKNOWN_LAYER_SOURCE = 'unknown';

export const runProjectSuccessForensics = (
  project: Project,
  options: ProjectSuccessForensicsOptions = {},
): ProjectSuccessAnalysis => {
  const projectRecord = project as unknown as Record<string, unknown>;
  const derivedProjectName =
    typeof projectRecord.projectName === 'string'
      ? (projectRecord.projectName as string)
      : typeof projectRecord.name === 'string'
        ? (projectRecord.name as string)
        : null;
  const projectName = options.projectName ?? derivedProjectName ?? 'Unnamed Project';
  const workspaces = project.workspaces ?? [];

  if (workspaces.length === 0) {
    return {
      projectName,
      analyzedWorkspaces: [],
    };
  }

  const versionsById = buildVersionIndex(workspaces);
  const layersById = buildLayerMap((project.layers ?? []) as ProjectLayer[]);
  const generationTasks =
    (project as unknown as { generationTasks?: GenerationTask[] }).generationTasks ?? [];
  const generationTaskIndex = buildGenerationTaskIndex(generationTasks);
  const snapshots = options.snapshots ?? [];
  const snapshotIndex = buildSnapshotIndex(snapshots);

  const analyzedWorkspaces: ProjectSuccessWorkspaceAnalysis[] = [];

  for (const workspace of workspaces) {
    const analysis = analyzeWorkspace(workspace, versionsById, layersById, generationTaskIndex, snapshotIndex);
    if (analysis) {
      analyzedWorkspaces.push(analysis);
    }
  }

  return {
    projectName,
    analyzedWorkspaces,
  };
};

const analyzeWorkspace = (
  workspace: ProjectWorkspace,
  versionsById: Map<string, WorkspaceVersion>,
  layersById: Map<string, StrippedLayer>,
  generationTaskIndex: Map<string, GenerationTask[]>,
  snapshotIndex: Map<string, GenerationTaskSnapshot>,
): ProjectSuccessWorkspaceAnalysis | null => {
  const isWorkspaceVisible =
    workspace.visible === undefined || workspace.visible === null ? true : Boolean(workspace.visible);
  if (!isWorkspaceVisible) {
    return null;
  }

  const validatedVersionId =
    typeof workspace.validatedVersionId === 'string' ? workspace.validatedVersionId : null;
  if (!validatedVersionId) {
    return null;
  }

  const validatedVersion = versionsById.get(validatedVersionId);
  if (!validatedVersion) {
    return null;
  }

  const lineage = collectParentChain(validatedVersionId, versionsById);
  if (lineage.length === 0) {
    return null;
  }

  const layerGenealogies = buildWorkspaceLayerGenealogies(
    workspace,
    lineage,
    validatedVersion,
    layersById,
    generationTaskIndex,
    snapshotIndex,
  );

  if (layerGenealogies.length === 0) {
    return null;
  }

  const initialVersion = lineage.length > 0 ? toVersionInfo(lineage[0]!) : null;

  return {
    workspaceId: workspace.id,
    workspaceName: workspace.name,
    validatedVersion: toVersionInfo(validatedVersion),
    initialVersion,
    layerGenealogies,
  };
};

const buildWorkspaceLayerGenealogies = (
  workspace: ProjectWorkspace,
  lineage: WorkspaceVersion[],
  validatedVersion: WorkspaceVersion,
  layersById: Map<string, StrippedLayer>,
  generationTaskIndex: Map<string, GenerationTask[]>,
  snapshotIndex: Map<string, GenerationTaskSnapshot>,
): ProjectSuccessLayerGenealogy[] => {
  const validatedVisibleLayerIds = extractVisibleLayerIds(validatedVersion);
  if (validatedVisibleLayerIds.size === 0) {
    return [];
  }

  const visibilityMap = buildVisibilityMap(lineage);
  const versionByParentId = buildVersionByParentIdIndex(lineage);

  const genealogies: ProjectSuccessLayerGenealogy[] = [];

  for (const layerId of validatedVisibleLayerIds) {
    const baseFinalLayer = layersById.get(layerId);
    const finalLayerSnapshot = baseFinalLayer
      ? cloneLayer(baseFinalLayer)
      : createPlaceholderLayer(layerId, workspace.id);

    const genealogy = buildLayerGenealogy(
      layerId,
      lineage,
      validatedVersion.id,
      visibilityMap,
      versionByParentId,
      generationTaskIndex,
      snapshotIndex,
      finalLayerSnapshot,
      workspace.id,
    );

    if (genealogy) {
      genealogies.push(genealogy);
    }
  }

  genealogies.sort((a, b) => {
    const aTimestamp = typeof a.layer.timestamp === 'number' ? a.layer.timestamp : 0;
    const bTimestamp = typeof b.layer.timestamp === 'number' ? b.layer.timestamp : 0;
    if (aTimestamp !== bTimestamp) {
      return aTimestamp - bTimestamp;
    }
    return a.layer.id.localeCompare(b.layer.id);
  });

  return genealogies;
};

const buildLayerGenealogy = (
  layerId: string,
  lineage: WorkspaceVersion[],
  validatedVersionId: string,
  visibilityMap: Map<string, Set<string>>,
  versionByParentId: Map<string | null, WorkspaceVersion>,
  generationTaskIndex: Map<string, GenerationTask[]>,
  snapshotIndex: Map<string, GenerationTaskSnapshot>,
  finalLayerSnapshot: StrippedLayer,
  workspaceId: string,
): ProjectSuccessLayerGenealogy | null => {
  const visibleVersions = lineage.filter((version) => {
    const visibleSet = visibilityMap.get(version.id);
    return visibleSet?.has(layerId) ?? false;
  });

  if (visibleVersions.length === 0) {
    return null;
  }

  const tasksForLayer = generationTaskIndex.get(layerId) ?? [];
  const tasksByVersionId = new Map<string, GenerationTask[]>();

  for (const task of tasksForLayer) {
    const targetVersion = versionByParentId.get(task.parentVersionId ?? null);
    if (!targetVersion) {
      continue;
    }
    const targetVisibility = visibilityMap.get(targetVersion.id);
    if (!targetVisibility?.has(layerId)) {
      continue;
    }
    const bucket = tasksByVersionId.get(targetVersion.id);
    if (bucket) {
      bucket.push(task);
    } else {
      tasksByVersionId.set(targetVersion.id, [task]);
    }
  }

  for (const bucket of tasksByVersionId.values()) {
    bucket.sort((a, b) => getTaskTimestamp(a) - getTaskTimestamp(b));
  }

  const steps: ProjectSuccessLayerTimelineStep[] = [];
  let hasPriorVisibility = false;

  for (const version of visibleVersions) {
    const tasksForVersion = tasksByVersionId.get(version.id) ?? [];
    const representativeTask =
      tasksForVersion.length > 0 ? tasksForVersion[tasksForVersion.length - 1] : undefined;
    const resolvedLayerSnapshot = resolveLayerSnapshot(
      tasksForVersion,
      finalLayerSnapshot,
      workspaceId,
    );

    const versionContext =
      ((version as unknown as { generativeContext?: unknown }).generativeContext as Record<
        string,
        unknown
      > | undefined) ?? undefined;

    const timestampFallback =
      typeof resolvedLayerSnapshot.timestamp === 'number'
        ? resolvedLayerSnapshot.timestamp
        : representativeTask
          ? getTaskTimestamp(representativeTask)
          : typeof version.timestamp === 'number'
            ? version.timestamp
            : undefined;

    let snapshot = snapshotIndex.get(version.id);
  
    if (snapshot) {
      const hasExplicitTask = tasksForVersion.length > 0;

      if (!hasExplicitTask) {
        // If we don't have an explicit task linking this layer to this version (missing history),
        // we use heuristics to decide if this snapshot belongs to this layer.

        // Heuristic 1: Layer is newly introduced in this version (Creation)
        const isIntroduction = !hasPriorVisibility;

        // Heuristic 2: Snapshot rect matches layer rect.
        // This helps for in-place updates (Inpainting/Generative Fill) where the layer hasn't moved.
        // If the layer was moved later, this might fail, but it prevents cross-contamination.
        const rectMatch = isRectMatch(snapshot.rect, finalLayerSnapshot.rect);

        if (!isIntroduction && !rectMatch) {
          snapshot = undefined;
        }
      }
    }

    const layerInfo = toLayerInfo(resolvedLayerSnapshot, versionContext, representativeTask, timestampFallback, snapshot);
    const changeType: ProjectSuccessLayerChangeType =
      !hasPriorVisibility
        ? 'INTRODUCED'
        : tasksForVersion.length > 0
          ? 'UPDATED'
          : 'PERSISTED';

    steps.push({
      version: toVersionInfo(version),
      layerState: layerInfo,
      changeType,
      contributingTaskIds: tasksForVersion.map((task) => task.id),
      isFinal: version.id === validatedVersionId,
    });

    hasPriorVisibility = true;
  }

  if (steps.length === 0) {
    return null;
  }

  steps.sort((a, b) => {
    const aTs = typeof a.version.timestamp === 'number' ? a.version.timestamp : 0;
    const bTs = typeof b.version.timestamp === 'number' ? b.version.timestamp : 0;
    if (aTs !== bTs) {
      return aTs - bTs;
    }
    return a.version.id.localeCompare(b.version.id);
  });

  const finalLayerState = steps[steps.length - 1]?.layerState ?? toLayerInfo(finalLayerSnapshot);

  return {
    layer: finalLayerState,
    timeline: steps,
  };
};

const isRectMatch = (r1?: Rect | null, r2?: Rect | null): boolean => {
  if (!r1 || !r2) return false;
  // Allow small tolerance for floating point or rounding differences
  return (
    Math.abs(r1.x - r2.x) < 2 &&
    Math.abs(r1.y - r2.y) < 2 &&
    Math.abs(r1.w - r2.w) < 2 &&
    Math.abs(r1.h - r2.h) < 2
  );
};

const buildVersionIndex = (workspaces: readonly ProjectWorkspace[]): Map<string, WorkspaceVersion> => {
  const map = new Map<string, WorkspaceVersion>();
  for (const workspace of workspaces) {
    for (const version of workspace.versions ?? []) {
      map.set(version.id, version);
    }
  }
  return map;
};

const buildVisibilityMap = (versions: WorkspaceVersion[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  for (const version of versions) {
    map.set(version.id, extractVisibleLayerIds(version));
  }
  return map;
};

const buildVersionByParentIdIndex = (
  versions: WorkspaceVersion[],
): Map<string | null, WorkspaceVersion> => {
  const map = new Map<string | null, WorkspaceVersion>();
  for (const version of versions) {
    map.set(version.parentId ?? null, version);
  }
  return map;
};

const extractVisibleLayerIds = (version: WorkspaceVersion): Set<string> => {
  const visible = new Set<string>();
  for (const layerRef of version.layerVisibility ?? []) {
    if (layerRef.visible) {
      visible.add(layerRef.id);
    }
  }
  return visible;
};

const resolveLayerSnapshot = (
  tasks: readonly GenerationTask[],
  finalLayerSnapshot: StrippedLayer,
  workspaceId: string,
): StrippedLayer => {
  for (let index = tasks.length - 1; index >= 0; index -= 1) {
    const candidate = extractLayerFromTask(tasks[index] as GenerationTask, finalLayerSnapshot, workspaceId);
    if (candidate) {
      return candidate;
    }
  }
  return cloneLayer(finalLayerSnapshot);
};

const extractLayerFromTask = (
  task: GenerationTask,
  fallback: StrippedLayer,
  workspaceId: string,
): StrippedLayer | null => {
  if (task.layer && typeof task.layer.id === 'string') {
    // Merge onto fallback to preserve fields (like reconstructedInputUrl) that may not be present on historical snapshots
    const merged = { ...cloneLayer(fallback), ...cloneLayer(task.layer) };
    return merged;
  }

  if (task.layerOverride) {
    return mergeLayerOverride(fallback, task.layerOverride, workspaceId);
  }

  return null;
};

const mergeLayerOverride = (
  base: StrippedLayer,
  override: Partial<Layer>,
  workspaceId: string,
): StrippedLayer => {
  const strippedOverride = stripPartialLayer(override);
  const merged = { ...cloneLayer(base), ...strippedOverride };

  if (typeof merged.layerWorkspaceId !== 'string' || merged.layerWorkspaceId.length === 0) {
    merged.layerWorkspaceId = base.layerWorkspaceId || workspaceId;
  }

  if (!merged.source || merged.source.trim().length === 0) {
    const overrideSource =
      typeof strippedOverride.source === 'string' && strippedOverride.source.trim().length > 0
        ? strippedOverride.source
        : null;
    merged.source = overrideSource ?? base.source ?? UNKNOWN_LAYER_SOURCE;
  }

  if (!merged.name || merged.name.trim().length === 0) {
    merged.name = base.name ?? `Layer ${base.id}`;
  }

  return merged;
};

const stripPartialLayer = (layer: Partial<Layer>): Partial<StrippedLayer> => {
  if (!layer) {
    return {};
  }
  const { imageData, ...rest } = layer;
  return rest;
};

const cloneLayer = (layer: Layer | StrippedLayer): StrippedLayer => {
  if ('imageData' in layer) {
    const { imageData, ...rest } = layer;
    return { ...rest };
  }
  return { ...layer };
};

const createPlaceholderLayer = (layerId: string, workspaceId: string): StrippedLayer => ({
  id: layerId,
  rect: createZeroRect(),
  visible: true,
  name: `Layer ${layerId}`,
  locked: false,
  isAltered: false,
  layerWorkspaceId: workspaceId,
  source: UNKNOWN_LAYER_SOURCE,
});

const createZeroRect = (): Rect => ({
  x: 0,
  y: 0,
  w: 0,
  h: 0,
});

const toLayerInfo = (
  layer: StrippedLayer,
  fallbackContext?: Record<string, unknown>,
  generationTask?: GenerationTask,
  timestampFallback?: number,
  snapshot?: GenerationTaskSnapshot,
): ProjectSuccessLayerInfo => {
  const info: ProjectSuccessLayerInfo = {
    id: layer.id,
    name: layer.name ?? `Layer ${layer.id}`,
    timestamp: typeof layer.timestamp === 'number' ? layer.timestamp : timestampFallback,
    source: layer.source ?? UNKNOWN_LAYER_SOURCE,
    imageSrc: layer.imageSrc,
    reconstructedInputUrl: layer.reconstructedInputUrl,
    generation: extractLayerGenerationMetadata(layer, fallbackContext, generationTask, snapshot),
    modificationLog: layer.modificationLog ? layer.modificationLog.map((entry) => ({ ...entry })) : undefined,
  };

  return info;
};

const extractLayerGenerationMetadata = (
  layer: StrippedLayer | null,
  fallbackContext?: Record<string, unknown>,
  generationTask?: GenerationTask,
  snapshot?: GenerationTaskSnapshot,
): ProjectSuccessLayerGenerationMetadata => {
  const layerParams = (layer?.params as DiffusionParams | undefined) ?? null;
  const layerParamsRecord = layerParams ? (layerParams as unknown as Record<string, unknown>) : null;
  const taskParams = (generationTask?.params as DiffusionParams | undefined) ?? null;
  const taskParamsRecord = taskParams ? (taskParams as unknown as Record<string, unknown>) : null;
  const snapshotParams = (snapshot?.params as DiffusionParams | undefined) ?? null;
  const snapshotParamsRecord = snapshotParams ? (snapshotParams as unknown as Record<string, unknown>) : null;

  const extractModelFromParams = (record?: Record<string, unknown> | null): string | null => {
    if (!record) {
      return null;
    }
    if (typeof record.model === 'string' && record.model.trim().length > 0) {
      return record.model.trim();
    }
    const candidateKeys = [
      'modelId',
      'model_id',
      'modelName',
      'model_name',
      'diffusionModel',
      'selectedModel',
    ];
    for (const key of candidateKeys) {
      const value = record[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    for (const [key, value] of Object.entries(record)) {
      if (typeof value === 'string' && /model/i.test(key) && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  };

  const promptFromLayerParams =
    layerParamsRecord && typeof layerParamsRecord.prompt === 'string' ? layerParamsRecord.prompt : null;
  const promptFromTaskParams =
    taskParamsRecord && typeof taskParamsRecord.prompt === 'string' ? taskParamsRecord.prompt : null;
  const promptFromSnapshotParams =
    snapshotParamsRecord && typeof snapshotParamsRecord.prompt === 'string' ? snapshotParamsRecord.prompt : null;
  const promptFromContext =
    fallbackContext && typeof fallbackContext.prompt === 'string' ? (fallbackContext.prompt as string) : null;

  const modelFromLayerParams = extractModelFromParams(layerParamsRecord);
  const modelFromTaskParams = extractModelFromParams(taskParamsRecord);
  const modelFromSnapshotParams = extractModelFromParams(snapshotParamsRecord);
  const modelFromTaskField =
    generationTask && typeof generationTask.model === 'string' && generationTask.model.trim().length > 0
      ? generationTask.model.trim()
      : null;
  const modelFromSnapshotField =
    snapshot && typeof snapshot.modelName === 'string' && snapshot.modelName.trim().length > 0
      ? snapshot.modelName.trim()
      : null;
  const modelFromContext =
    fallbackContext && typeof fallbackContext.model === 'string'
      ? (fallbackContext.model as string)
      : null;

  const resolvedPrompt = promptFromSnapshotParams ?? promptFromLayerParams ?? promptFromTaskParams ?? promptFromContext ?? null;
  const resolvedModel = modelFromSnapshotField ?? modelFromSnapshotParams ?? modelFromLayerParams ?? modelFromTaskParams ?? modelFromTaskField ?? modelFromContext ?? null;
  const rect = snapshot?.rect ?? layer?.rect ?? generationTask?.rect ?? null;
  const rawParams = snapshotParams ?? layerParams ?? taskParams ?? null;

  const hasInputAssets =
    generationTask &&
    (generationTask.mainImagePrivateUrl ||
      generationTask.maskPrivateUrl ||
      generationTask.extraImageUrlPrivate ||
      (generationTask.referenceImagePrivateUrls && generationTask.referenceImagePrivateUrls.length > 0));

  const inputAssets = snapshot
    ? {
        main: normalizeSnapshotUrl(snapshot.mainImagePrivateUrl),
        mask: normalizeSnapshotUrl(snapshot.maskPrivateUrl),
        control: normalizeSnapshotUrl(snapshot.extraImageUrlPrivate),
        references: snapshot.referenceImagePrivateUrls?.map(normalizeSnapshotUrl).filter((u): u is string => u !== null) ?? null,
      }
    : hasInputAssets
      ? {
          main: normalizeSnapshotUrl(generationTask?.mainImagePrivateUrl),
          mask: normalizeSnapshotUrl(generationTask?.maskPrivateUrl),
          control: normalizeSnapshotUrl(generationTask?.extraImageUrlPrivate),
          references: generationTask?.referenceImagePrivateUrls?.map(normalizeSnapshotUrl).filter((u): u is string => u !== null) ?? null,
        }
      : null;

  return {
    model: resolvedModel,
    prompt: resolvedPrompt,
    rect,
    rawParams,
    trace: {
      snapshotId: snapshot?.id ?? null,
      taskId: generationTask?.id ?? null,
      taskStatus: snapshot?.taskStatus ?? null,
      generationLogId: snapshot?.generationLogId ?? null,
      providerJobId: snapshot?.providerJobId ?? null,
      chatSessionId: snapshot?.chatSessionId ?? generationTask?.chatSessionId ?? null,
      workspaceId: snapshot?.workspaceId ?? generationTask?.workspaceId ?? null,
      projectId: snapshot?.projectId ?? generationTask?.projectId ?? null,
      acceptedVersionId: snapshot?.acceptedVersionId ?? null,
      candidateVersionId: snapshot?.candidateVersionId ?? null,
    },
    inputAssets,
  };
};

const toVersionInfo = (version: WorkspaceVersion): ProjectSuccessVersionInfo => ({
  id: version.id,
  name: version.name,
  timestamp: version.timestamp,
  parentId: version.parentId ?? null,
  generativeContext: (version as unknown as { generativeContext?: unknown }).generativeContext,
  imageUrl: version.imageUrl ?? null,
});

const buildLayerMap = (layers: ProjectLayer[]): Map<string, StrippedLayer> => {
  const map = new Map<string, StrippedLayer>();
  for (const layer of layers) {
    if (!layer || typeof layer.id !== 'string') {
      continue;
    }
    map.set(layer.id, cloneLayer(layer));
  }
  return map;
};

const buildGenerationTaskIndex = (tasks: readonly GenerationTask[]): Map<string, GenerationTask[]> => {
  const index = new Map<string, GenerationTask[]>();
  for (const task of tasks) {
    const layerId = resolveTaskLayerId(task);
    if (layerId === null) {
      continue;
    }
    const bucket = index.get(layerId);
    if (bucket) {
      bucket.push(task);
    } else {
      index.set(layerId, [task]);
    }
  }
  return index;
};

const buildSnapshotIndex = (snapshots: GenerationTaskSnapshot[]): Map<string, GenerationTaskSnapshot> => {
  const map = new Map<string, GenerationTaskSnapshot>();
  for (const snap of snapshots) {
    if (snap.candidateVersionId) {
      map.set(snap.candidateVersionId, snap);
    }
  }
  return map;
};

const resolveTaskLayerId = (task: GenerationTask): string | null => {
  if (typeof task.layer?.id === 'string') {
    return task.layer.id;
  }
  const overrideId = (task.layerOverride as { id?: string } | undefined)?.id;
  if (typeof overrideId === 'string') {
    return overrideId;
  }
  return null;
};

const getTaskTimestamp = (task: GenerationTask): number => {
  const layerTimestamp =
    typeof task.layer?.timestamp === 'number' ? task.layer.timestamp : null;
  if (layerTimestamp !== null) {
    return layerTimestamp;
  }
  const overrideTimestamp =
    typeof (task.layerOverride as { timestamp?: number } | undefined)?.timestamp === 'number'
      ? (task.layerOverride as { timestamp: number }).timestamp
      : null;
  if (overrideTimestamp !== null) {
    return overrideTimestamp;
  }
  return 0;
};

const collectParentChain = (
  startId: string | null,
  versionsById: Map<string, WorkspaceVersion>,
): WorkspaceVersion[] => {
  const chain: WorkspaceVersion[] = [];
  const visited = new Set<string>();
  let currentId = startId;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const version = versionsById.get(currentId);
    if (!version) {
      break;
    }
    chain.unshift(version);
    currentId = version.parentId;
  }

  return chain;
};

